import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-conditions-page">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#1B3A5F] mb-4">Terms & Conditions</h1>
          <p className="text-slate-600">Last updated: March 19, 2026</p>
        </div>

        <div className="legal-content prose prose-slate max-w-none">
          <h2>1. Business Identity and Services</h2>
          <p>
            These Terms and Conditions ("Terms") govern your use of the SMS text messaging services and website 
            provided by Auto Accident Claim Assist, a service helping South Carolina residents with auto accident claims.
          </p>
          <p>We offer the following services:</p>
          <ul>
            <li>Free auto accident case reviews</li>
            <li>Connection with licensed South Carolina attorneys</li>
            <li>Case follow-up and coordination</li>
            <li>Claim-related support and guidance</li>
          </ul>
          <p>
            By opting in to receive SMS text messages from us, you agree to these Terms and our{' '}
            <Link to="/privacy-policy" className="text-[#1B3A5F] hover:underline">Privacy Policy</Link>.
          </p>

          <h2>2. Types of Messages</h2>
          <p>When you opt in to our SMS messaging service, you may receive the following types of messages:</p>
          
          <h3>Non-Marketing Messages (Service-Related)</h3>
          <ul>
            <li>Case follow-ups and status updates</li>
            <li>Intake updates and confirmations</li>
            <li>Appointment scheduling and reminders</li>
            <li>Claim-related notifications</li>
            <li>Responses to your inquiries</li>
            <li>Customer support communications</li>
          </ul>

          <h3>Marketing Messages (Promotional)</h3>
          <p>Only if you explicitly consent, you may also receive:</p>
          <ul>
            <li>Special offers and discounts</li>
            <li>Information about new services</li>
            <li>Promotional content and updates</li>
          </ul>
          <p><strong>Important:</strong> Marketing messages require separate consent and are completely optional.</p>

          <h2>3. Message Frequency</h2>
          <p>
            Message frequency may vary depending on your case status and the type of communications you have opted into. 
            Typically, you can expect:
          </p>
          <ul>
            <li>Case-related messages as needed during your review process</li>
            <li>Occasional service updates as needed</li>
            <li>Marketing messages (if opted in): No more than 4 per month</li>
          </ul>

          <h2>4. Message and Data Rates</h2>
          <p>
            Message and data rates may apply. Standard messaging rates from your mobile carrier apply to all SMS 
            messages sent to and from our service. Contact your wireless provider for details about your messaging plan.
          </p>
          <p className="bg-slate-100 p-4 rounded-lg">
            As always, message and data rates may apply for any messages sent to you from us and to us from you. 
            Message frequency may vary. If you have any questions about your text plan or data plan, it is best to 
            contact your wireless provider.
          </p>

          <h2>5. How to Opt Out (STOP)</h2>
          <p className="bg-slate-100 p-4 rounded-lg">
            You can cancel the SMS service at any time. Just text "STOP" to +1 803-403-0488. After you send the SMS 
            message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed. After 
            this, you will no longer receive SMS messages from us.
          </p>
          <p>
            If you want to join again, just sign up as you did the first time and we will start sending SMS messages 
            to you again.
          </p>
          <p>
            If you are experiencing issues with the messaging program you can reply with the keyword HELP for more 
            assistance, or you can get help directly at brendan@autoaccidentclaimassist.com or +1 803-403-0488.
          </p>

          <h2>6. How to Get Help (HELP)</h2>
          <p>
            If you are experiencing issues with the messaging program, you can reply with the keyword "HELP" for 
            more assistance.
          </p>
          <p>You can also get help directly by:</p>
          <ul>
            <li>Email: brendan@autoaccidentclaimassist.com</li>
            <li>Phone: +1 803-403-0488</li>
          </ul>

          <h2>7. Carrier Liability Disclaimer</h2>
          <p className="bg-slate-100 p-4 rounded-lg font-semibold">
            Carriers are not liable for delayed or undelivered messages.
          </p>
          <p>
            Message delivery is subject to effective transmission from your network operator. Auto Accident Claim 
            Assist is not responsible for messages that are delayed or not delivered due to:
          </p>
          <ul>
            <li>Network congestion or technical issues</li>
            <li>Carrier service interruptions</li>
            <li>Device incompatibility</li>
            <li>Incorrect phone number provided</li>
            <li>Other factors outside our control</li>
          </ul>

          <h2>8. Consent and Authorization</h2>
          <p>You must be 18 years of age or older to opt in to our SMS messaging service.</p>
          <p>
            By providing your mobile phone number and checking the consent boxes on our form, you expressly consent 
            to receive automated text messages from Auto Accident Claim Assist at the phone number provided.
          </p>
          <p>
            Consent is not required as a condition of purchasing any goods or services. You may revoke your consent 
            at any time by texting STOP.
          </p>

          <h2>9. Privacy and Data Protection</h2>
          <p>
            Your personal information, including your phone number and consent preferences, is protected in accordance 
            with our <Link to="/privacy-policy" className="text-[#1B3A5F] hover:underline">Privacy Policy</Link>. We do 
            not sell, rent, or share your mobile information with third parties for marketing purposes.
          </p>
          <p>
            If you have any questions regarding privacy, please read our{' '}
            <Link to="/privacy-policy" className="text-[#1B3A5F] hover:underline">Privacy Policy</Link>.
          </p>

          <h2>10. Changes to These Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
            to our website. Your continued participation in our SMS program after any changes constitutes acceptance 
            of the updated Terms.
          </p>
          <p>We encourage you to review these Terms periodically for any updates.</p>

          <h2>11. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of South Carolina 
            and the federal laws of the United States applicable therein, without regard to conflict of law principles.
          </p>

          <h2>12. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall 
            continue in full force and effect.
          </p>

          <h2>13. Contact Information</h2>
          <p>For questions about these Terms or our SMS messaging program, please contact us:</p>
          <p><strong>Auto Accident Claim Assist</strong></p>
          <p>South Carolina</p>
          <p>Email: brendan@autoaccidentclaimassist.com</p>
          <p>Phone: +1 803-403-0488</p>

          <div className="mt-8 p-6 bg-[#1B3A5F]/5 rounded-lg border border-[#1B3A5F]/20">
            <h3 className="text-xl font-bold text-[#1B3A5F] mb-4">Quick Reference Summary</h3>
            <div className="space-y-3">
              <p><strong>To Opt Out:</strong></p>
              <p>Text STOP to +1 803-403-0488</p>
              <p><strong>For Help:</strong></p>
              <p>Text HELP or call +1 803-403-0488</p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <h3>Related Documents</h3>
            <ul>
              <li>
                <Link to="/privacy-policy" className="text-[#1B3A5F] hover:underline" data-testid="terms-privacy-link">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a href="https://autoaccidentclaimassist.com" className="text-[#1B3A5F] hover:underline" target="_blank" rel="noopener noreferrer">
                  Main Website
                </a>
              </li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
